import { useEffect, useState } from 'react'
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from 'recharts'
import {
  CloudSun, Umbrella, Drop, Recycle, Wind, Tree,
  DownloadSimple, CheckCircle, Warning, XCircle, Info, FileCsv
} from '@phosphor-icons/react'
import { supabase } from '../../lib/supabase'
import { downloadCSV } from '../esg/exportHelpers'

const ACCENT = {
  coral:    '#D4614A',
  slate:    '#7A8FA0',
  olive:    '#8D9A2E',
  amber:    '#C98A2E',
  charcoal: '#4A4A4A',
}

const CARD = {
  background: '#FFFFFF',
  borderRadius: '8px',
  padding: '1.5rem',
  boxShadow: '0 1px 4px rgba(59,47,47,0.07)',
  border: '1px solid #DDD5C8',
}

const STATUS_CONFIG = {
  contribucion: { label: 'Contribución sustancial', color: ACCENT.olive,    icon: CheckCircle },
  dnsh:         { label: 'DNSH cumplido',           color: ACCENT.amber,    icon: Info },
  riesgo:       { label: 'Riesgo incumplimiento',   color: ACCENT.coral,    icon: XCircle },
}

// ── Mapeo de Objetivos Taxonomía UE → categorías BREEAM ──────────────────────
const OBJECTIVE_DEFS = [
  {
    id: 1,
    name: 'Mitigación del cambio climático',
    icon: CloudSun,
    description: 'Reducción de emisiones de GEI, eficiencia energética, uso de energías renovables.',
    categories: ['Energía'],
    thresholdContribucion: 0.55,
    thresholdDNSH: 0.20,
  },
  {
    id: 2,
    name: 'Adaptación al cambio climático',
    icon: Umbrella,
    description: 'Resiliencia frente a riesgos climáticos físicos presentes y futuros.',
    categories: ['Salud y Bienestar'],
    thresholdContribucion: 0.50,
    thresholdDNSH: 0.20,
    proxyNote: 'Proxy via Salud y Bienestar — BREEAM ES no dispone de categoría directa de adaptación climática.',
  },
  {
    id: 3,
    name: 'Uso sostenible y protección del agua',
    icon: Drop,
    description: 'Eficiencia hídrica, protección de masas de agua, gestión de aguas pluviales.',
    categories: ['Agua'],
    thresholdContribucion: 0.55,
    thresholdDNSH: 0.20,
  },
  {
    id: 4,
    name: 'Economía circular',
    icon: Recycle,
    description: 'Uso eficiente de recursos, diseño para la deconstrucción, gestión de residuos de construcción.',
    categories: ['Materiales', 'Residuos'],
    thresholdContribucion: 0.50,
    thresholdDNSH: 0.20,
  },
  {
    id: 5,
    name: 'Prevención y control de la contaminación',
    icon: Wind,
    description: 'Evitar contaminación del aire, agua y suelo durante construcción y uso.',
    categories: ['Contaminación'],
    thresholdContribucion: 0.55,
    thresholdDNSH: 0.20,
  },
  {
    id: 6,
    name: 'Protección y restauración de la biodiversidad',
    icon: Tree,
    description: 'Preservación de ecosistemas, mejora del valor ecológico del emplazamiento.',
    categories: ['Uso del Suelo y Ecología'],
    thresholdContribucion: 0.55,
    thresholdDNSH: 0.20,
  },
]

// ── Calcula el estado real de cada objetivo desde las evaluaciones + datos reales (sensors/CI) ───
function computeObjectives(evaluations, BREEAM_CRITERIA, liveData) {
  const evals = evaluations || {}
  const live = liveData || {}

  const getCatData = (...catNames) => {
    let obtained = 0, max = 0
    const criteriaList = []
    catNames.forEach(catName => {
      const cat = BREEAM_CRITERIA[catName]
      if (!cat) return
      max += cat.maxPoints
      Object.entries(cat.criteria).forEach(([code, c]) => {
        const ev = evals[`${catName}-${code}`]
        const pts = (ev?.puntos_auditoria ?? ev?.points_obtained) || 0
        obtained += pts
        criteriaList.push({
          code,
          name: c.name,
          pts,
          maxPts: c.points,
          evaluated: !!ev,
          cumple: ev?.cumple,
        })
      })
    })
    const pct = max > 0 ? obtained / max : 0
    const hasEvals = criteriaList.some(c => c.evaluated)
    return { obtained, max, pct, hasEvals, criteriaList }
  }

  const getStatus = (pct, hasEvals, def) => {
    if (!hasEvals) return 'riesgo'
    if (pct >= def.thresholdContribucion) return 'contribucion'
    if (pct >= def.thresholdDNSH) return 'dnsh'
    return 'riesgo'
  }

  return OBJECTIVE_DEFS.map(def => {
    const data = getCatData(...def.categories)
    let status = getStatus(data.pct, data.hasEvals, def)
    const pctInt = Math.round(data.pct * 100)

    // ── Override con datos reales cuando aplican ──
    let liveBadge = null
    let liveDetail = null

    // Obj 1 — Mitigación CC: usa intensidad CO2 (kgCO2/m²·año) si hay sensores
    if (def.id === 1 && live.sensors?.emissionsIntensity != null) {
      const ei = live.sensors.emissionsIntensity
      // CTE-HE0 nZEB: < 4 kgCO2/m² → contribución | <10 DNSH | resto riesgo
      if (ei < 4) status = 'contribucion'
      else if (ei < 10) status = 'dnsh'
      else status = 'riesgo'
      liveBadge = `${ei.toFixed(2)} kgCO₂e/m²·año`
      liveDetail = `Sensores: emisiones operacionales reales. Umbral Tax UE alineado con CTE-HE0/nZEB ES.`
    }
    // Obj 3 — Agua: usa intensidad m³/m² si hay sensores
    if (def.id === 3 && live.sensors?.waterIntensity != null) {
      const wi = live.sensors.waterIntensity
      if (wi < 0.5) status = 'contribucion'
      else if (wi < 1.0) status = 'dnsh'
      else status = 'riesgo'
      liveBadge = `${wi.toFixed(2)} m³/m²·año`
      liveDetail = `Sensores: consumo operacional real. Hotel ES medio: 0,8-1,5 m³/m²·año.`
    }
    // Obj 4 — Econ Circular: usa CI DGNB si está guardado
    if (def.id === 4 && live.ci != null) {
      if (live.ci >= 0.60) status = 'contribucion'
      else if (live.ci >= 0.45) status = 'dnsh'
      else status = 'riesgo'
      liveBadge = `CI = ${live.ci.toFixed(2)}`
      liveDetail = `Building Resource Passport DGNB. Umbral Tax UE obj. 4 = CI ≥ 0,45.`
    }

    let detail
    if (liveDetail) {
      detail = liveDetail
      if (data.hasEvals) detail += ` Soporte BREEAM: ${data.obtained}/${data.max} pts en ${def.categories.join(' + ')}.`
    } else if (!data.hasEvals) {
      detail = `Sin evaluaciones BREEAM registradas en ${def.categories.join(' + ')}. Guarda criterios en esas categorías o carga sensores/CI para calcular automáticamente.`
    } else {
      detail = `Proxy: ${pctInt}% de los puntos BREEAM obtenidos (${data.obtained}/${data.max} pts) en ${def.categories.join(' + ')}.`
      if (def.proxyNote) detail += ` ${def.proxyNote}`
    }

    return {
      ...def,
      status,
      detail,
      pctInt,
      pts: { obtained: data.obtained, max: data.max },
      criteriaList: data.criteriaList,
      liveBadge,
    }
  })
}

function AlignmentGauge({ pct }) {
  const data = [{ value: pct, fill: ACCENT.olive }]
  return (
    <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <ResponsiveContainer width={220} height={140}>
        <RadialBarChart
          cx="50%" cy="80%"
          innerRadius="70%" outerRadius="100%"
          startAngle={180} endAngle={0}
          data={data}
          barSize={18}
        >
          <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
          <RadialBar
            background={{ fill: '#EDE7DE' }}
            dataKey="value"
            cornerRadius={8}
            angleAxisId={0}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div style={{ position: 'absolute', bottom: '0', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: ACCENT.olive, lineHeight: 1 }}>
          {pct}%
        </div>
        <div style={{ fontSize: '0.7rem', color: '#7A6A5A', marginTop: '0.2rem' }}>alineación estimada</div>
      </div>
    </div>
  )
}

function generateAlignmentReport({ project, breeamPct, alignmentPct, alignedCount, objectives }) {
  const date = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
  const projectName = project?.name || 'Proyecto'

  const STATUS_LABELS = { contribucion: 'Contribución sustancial', dnsh: 'DNSH cumplido', riesgo: 'Riesgo de incumplimiento' }
  const STATUS_COLORS = { contribucion: '#8D9A2E', dnsh: '#C98A2E', riesgo: '#D4614A' }

  const objectivesRows = objectives.map(o => `
    <tr>
      <td style="padding:10px 14px;border-bottom:1px solid #EDE7DE;font-weight:500;color:#2C2017">${o.id}. ${o.name}</td>
      <td style="padding:10px 14px;border-bottom:1px solid #EDE7DE">
        <span style="display:inline-flex;align-items:center;gap:6px;border:1px solid ${STATUS_COLORS[o.status]};color:${STATUS_COLORS[o.status]};border-radius:3px;padding:3px 8px;font-size:11px;font-weight:600">
          <span style="width:6px;height:6px;border-radius:50%;background:${STATUS_COLORS[o.status]};display:inline-block"></span>
          ${STATUS_LABELS[o.status]}
        </span>
      </td>
      <td style="padding:10px 14px;border-bottom:1px solid #EDE7DE;font-size:12px;color:#7A6A5A">${o.detail}</td>
    </tr>`).join('')

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>Informe Alineación Taxonomía UE — ${projectName}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'DM Sans',sans-serif; background:#F5F0EA; color:#2C2017; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    .page { max-width:860px; margin:0 auto; background:#FFF; }
    @media print {
      body { background:#FFF; }
      .no-print { display:none !important; }
      .page { box-shadow:none; }
    }
  </style>
</head>
<body>
<div class="page">
  <div style="background:#3B2F2F;padding:28px 36px;color:#F5F0EA">
    <div style="font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:#B0A89E;margin-bottom:6px">Reglamento UE 2020/852 · Taxonomía Europea</div>
    <h1 style="font-size:20px;font-weight:700;margin-bottom:4px">Informe de Alineación con la Taxonomía Europea</h1>
    <div style="font-size:13px;color:#B0A89E">${projectName} · Emitido el ${date}</div>
  </div>
  <div style="padding:24px 36px;background:#FAF7F3;border-bottom:1px solid #DDD5C8;display:flex;gap:40px;flex-wrap:wrap">
    <div>
      <div style="font-size:10px;color:#7A6A5A;text-transform:uppercase;letter-spacing:0.07em;margin-bottom:4px">Alineación estimada</div>
      <div style="font-size:32px;font-weight:700;font-family:monospace;color:#8D9A2E">${alignmentPct}%</div>
    </div>
    <div>
      <div style="font-size:10px;color:#7A6A5A;text-transform:uppercase;letter-spacing:0.07em;margin-bottom:4px">Objetivos con contr. sustancial</div>
      <div style="font-size:32px;font-weight:700;font-family:monospace;color:#2C2017">${alignedCount}/6</div>
    </div>
    <div>
      <div style="font-size:10px;color:#7A6A5A;text-transform:uppercase;letter-spacing:0.07em;margin-bottom:4px">BREEAM Score (proxy)</div>
      <div style="font-size:32px;font-weight:700;font-family:monospace;color:#C98A2E">${breeamPct}%</div>
    </div>
  </div>
  <div style="padding:24px 36px">
    <h2 style="font-size:14px;font-weight:700;color:#2C2017;margin-bottom:16px;text-transform:uppercase;letter-spacing:0.05em">Semáforo por Objetivo Medioambiental</h2>
    <table style="width:100%;border-collapse:collapse;font-size:13px">
      <thead>
        <tr style="background:#EDE7DE">
          <th style="padding:10px 14px;text-align:left;font-weight:600;color:#5C4A3A;font-size:11px;text-transform:uppercase;letter-spacing:0.05em">Objetivo</th>
          <th style="padding:10px 14px;text-align:left;font-weight:600;color:#5C4A3A;font-size:11px;text-transform:uppercase;letter-spacing:0.05em">Estado</th>
          <th style="padding:10px 14px;text-align:left;font-weight:600;color:#5C4A3A;font-size:11px;text-transform:uppercase;letter-spacing:0.05em">Evaluación</th>
        </tr>
      </thead>
      <tbody>${objectivesRows}</tbody>
    </table>
  </div>
  <div style="margin:0 36px 24px;background:#FAF7F3;border:1px solid #DDD5C8;border-left:3px solid #C98A2E;border-radius:4px;padding:14px 16px;font-size:11px;color:#7A6A5A;line-height:1.6">
    <strong style="color:#5C4A3A">Nota legal:</strong> El análisis de alineación con la Taxonomía Europea es una estimación indicativa basada en criterios BREEAM como proxy. La verificación formal de alineación Taxonomía UE requiere evaluación por Revisor Externo acreditado conforme al Reglamento (UE) 2020/852 y sus actos delegados. Este informe no tiene valor de certificación oficial.
  </div>
  <div style="background:#EDE7DE;padding:14px 36px;display:flex;justify-content:space-between;align-items:center">
    <span style="font-size:11px;color:#7A6A5A">BREEAM Analyzer · Informe generado el ${date}</span>
    <span style="font-size:11px;color:#7A6A5A">Basado en Reglamento (UE) 2020/852</span>
  </div>
  <div class="no-print" style="text-align:center;padding:20px;background:#F5F0EA">
    <button onclick="window.print()" style="background:#3B2F2F;color:#F5F0EA;border:none;border-radius:4px;padding:10px 24px;font-size:14px;font-weight:600;cursor:pointer;font-family:'DM Sans',sans-serif">
      Guardar como PDF (Ctrl+P)
    </button>
    <p style="margin-top:8px;font-size:11px;color:#7A6A5A">En el diálogo de impresión, selecciona "Guardar como PDF" como destino</p>
  </div>
</div>
</body>
</html>`

  const win = window.open('', '_blank')
  win.document.write(html)
  win.document.close()
  win.focus()
  setTimeout(() => win.print(), 800)
}

export default function TaxonomiaEuropea({ project, BREEAM_CRITERIA }) {
  const [expanded, setExpanded] = useState(null)
  const [liveData, setLiveData] = useState(null)
  const [liveLoading, setLiveLoading] = useState(false)

  const totalPoints = project?.totalPoints || 0
  const maxPoints = project?.maxPoints || 110
  const breeamPct = Math.round((totalPoints / maxPoints) * 100)

  // Carga datos reales: sensors agregados (Obj 1 emisiones, Obj 3 agua) + CI DGNB (Obj 4)
  useEffect(() => {
    let cancel = false
    async function loadLive() {
      if (!project?.id) return
      setLiveLoading(true)
      const [{ data: sensors }, { data: ciSnap }] = await Promise.all([
        supabase.from('building_sensors').select('*').eq('project_id', project.id),
        supabase.from('esg_snapshots').select('value_numeric, created_at').eq('project_id', project.id).eq('framework', 'circular').eq('indicator_code', 'DGNB_CI').order('created_at', { ascending: false }).limit(1),
      ])
      if (cancel) { setLiveLoading(false); return }
      // Surface m2 — sumar zonas ENE 1 si está, sino project.surface_m2
      let m2 = project?.surface_m2 || null
      const ene1j = (() => { try { return JSON.parse(project?.evaluations?.['Energía-ENE 1']?.justificacion || 'null') } catch { return null } })()
      const zonas = ene1j?.ene1_calculadora?.zonas
      if (Array.isArray(zonas)) {
        const z = zonas.filter(z => parseFloat(z.m2) > 0).reduce((s, z) => s + parseFloat(z.m2), 0)
        if (z > 0) m2 = z
      }
      // Agregar sensores del último año
      let sensorAgg = null
      if (sensors?.length) {
        const yMap = new Map()
        sensors.forEach(r => {
          const a = yMap.get(r.reading_year) || { year: r.reading_year, co2: 0, m3: 0, n: 0 }
          a.co2 += +(r.scope1_kgco2e || 0) + +(r.scope2_kgco2e || 0)
          a.m3 += +(r.water_m3_total || 0)
          a.n += 1
          yMap.set(r.reading_year, a)
        })
        const years = [...yMap.keys()].sort((a, b) => b - a)
        const y = yMap.get(years[0])
        sensorAgg = {
          year: y.year, months: y.n,
          emissionsIntensity: m2 ? y.co2 / m2 : null,    // kgCO2/m²·año
          waterIntensity: m2 ? y.m3 / m2 : null,         // m³/m²·año
        }
      }
      setLiveData({ sensors: sensorAgg, ci: ciSnap?.[0]?.value_numeric ?? null })
      setLiveLoading(false)
    }
    loadLive()
    return () => { cancel = true }
  }, [project?.id, project?.evaluations, project?.surface_m2])

  const objectives = computeObjectives(project?.evaluations, BREEAM_CRITERIA, liveData)
  const alignedCount = objectives.filter(o => o.status === 'contribucion').length
  const alignmentPct = Math.round((alignedCount / objectives.length) * 100)

  const handleDownloadPDF = () => generateAlignmentReport({ project, breeamPct, alignmentPct, alignedCount, objectives })
  const handleDownloadCSV = () => {
    downloadCSV(
      objectives.map(o => ({
        objetivo: o.id, nombre: o.name, estado: o.status,
        pct_breeam: o.pctInt, pts_obtenidos: o.pts.obtained, pts_max: o.pts.max,
        dato_real: o.liveBadge || '', categorias: o.categories.join(' + '),
      })),
      `TaxonomiaUE_${(project?.name || 'proyecto').replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', fontFamily: "'DM Sans', sans-serif" }}>

      {/* Resumen */}
      <div style={{ background: '#3B2F2F', borderRadius: '8px', padding: '1.5rem 2rem', color: '#F5F0EA', display: 'flex', alignItems: 'center', gap: '2.5rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#B0A89E', marginBottom: '0.3rem', fontWeight: '500' }}>
            Reglamento UE 2020/852 · Taxonomía Europea
          </div>
          <div style={{ fontSize: '1.2rem', fontWeight: '600', color: '#F5F0EA', marginBottom: '0.2rem' }}>
            Análisis de Alineación
          </div>
          <div style={{ fontSize: '0.8rem', color: '#B0A89E' }}>
            {alignedCount} de 6 objetivos con contribución sustancial · basado en evaluaciones guardadas
          </div>
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
            const count = objectives.filter(o => o.status === key).length
            return (
              <div key={key} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: cfg.color }}>{count}</div>
                <div style={{ fontSize: '0.65rem', color: '#B0A89E', marginTop: '0.1rem' }}>{cfg.label}</div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Grid semáforo + gauge */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1.25rem', alignItems: 'start' }}>

        {/* Semáforo de objetivos */}
        <div style={CARD}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
            Semáforo por Objetivo Medioambiental
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {objectives.map(obj => {
              const cfg = STATUS_CONFIG[obj.status]
              const ObjIcon = obj.icon
              const isExp = expanded === obj.id
              const evaluatedCount = obj.criteriaList.filter(c => c.evaluated).length
              const totalCount = obj.criteriaList.length
              return (
                <div key={obj.id} style={{ border: '1px solid #DDD5C8', borderRadius: '6px', overflow: 'hidden' }}>
                  <div
                    onClick={() => setExpanded(isExp ? null : obj.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.875rem',
                      padding: '0.875rem 1rem', cursor: 'pointer',
                      background: isExp ? '#FAF7F3' : '#FFFFFF',
                      borderLeft: `3px solid ${cfg.color}`,
                      transition: 'background 0.15s',
                    }}
                  >
                    <ObjIcon size={20} style={{ color: '#7A6A5A', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#2C2017' }}>{obj.name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#7A6A5A', marginTop: '0.1rem' }}>
                        {obj.description}
                      </div>
                    </div>
                    {/* Progreso BREEAM */}
                    <div style={{ textAlign: 'right', flexShrink: 0, minWidth: '80px' }}>
                      <div style={{ fontSize: '0.65rem', color: '#B0A89E', marginBottom: '0.2rem' }}>
                        {evaluatedCount}/{totalCount} criterios
                      </div>
                      <div style={{ height: '3px', background: '#EDE7DE', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ background: cfg.color, height: '100%', width: `${totalCount > 0 ? (evaluatedCount / totalCount) * 100 : 0}%`, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0, marginLeft: '0.5rem' }}>
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: cfg.color }} />
                      <span style={{ fontSize: '0.7rem', fontWeight: '500', color: cfg.color, whiteSpace: 'nowrap' }}>
                        {cfg.label}
                      </span>
                    </div>
                  </div>
                  {isExp && (
                    <div style={{ padding: '0.875rem 1rem 1rem', borderTop: '1px solid #EDE7DE', background: '#FAF7F3' }}>
                      <div style={{ fontSize: '0.8rem', color: '#5C4A3A', marginBottom: '0.875rem', lineHeight: '1.5' }}>
                        {obj.detail}
                      </div>
                      {/* Puntos obtenidos */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.875rem' }}>
                        <div style={{ background: 'white', border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '0.3rem 0.75rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.55rem', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Pts BREEAM</div>
                          <div style={{ fontSize: '1rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: cfg.color }}>
                            {obj.pts.obtained}<span style={{ fontSize: '0.65rem', color: '#B0A89E' }}>/{obj.pts.max}</span>
                          </div>
                        </div>
                        <div style={{ background: 'white', border: `1px solid ${cfg.color}`, borderRadius: '4px', padding: '0.3rem 0.75rem', textAlign: 'center' }}>
                          <div style={{ fontSize: '0.55rem', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.06em' }}>% obtenido</div>
                          <div style={{ fontSize: '1rem', fontWeight: '700', fontFamily: "'IBM Plex Mono', monospace", color: cfg.color }}>
                            {obj.pctInt}%
                          </div>
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#7A6A5A', flex: 1 }}>
                          Categorías BREEAM: <strong style={{ color: '#2C2017' }}>{obj.categories.join(', ')}</strong>
                        </div>
                      </div>
                      {/* Criterios individuales */}
                      <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#7A6A5A', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '0.5rem' }}>
                        Criterios BREEAM asociados
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {obj.criteriaList.map((c, i) => {
                          const ok = c.evaluated && c.cumple !== false
                          const pending = !c.evaluated
                          const failed = c.evaluated && c.cumple === false
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <div style={{
                                width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
                                background: ok ? ACCENT.olive : failed ? ACCENT.coral : '#DDD5C8'
                              }} />
                              <span style={{ fontSize: '0.775rem', color: ok ? '#4A5A10' : pending ? '#7A6A5A' : '#8B2A1A', flex: 1 }}>
                                <strong>{c.code}</strong> — {c.name}
                              </span>
                              <span style={{ fontSize: '0.65rem', fontFamily: "'IBM Plex Mono', monospace", color: '#B0A89E', flexShrink: 0 }}>
                                {c.evaluated ? `${c.pts}/${c.maxPts}` : '—'}
                              </span>
                            </div>
                          )
                        })}
                      </div>
                      {obj.criteriaList.length === 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#B0A89E', fontStyle: 'italic' }}>
                          No se encontraron criterios para esta categoría.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Gauge + indicators */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', minWidth: '280px' }}>
          <div style={{ ...CARD, textAlign: 'center' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017', textAlign: 'left' }}>
              Alineación Estimada
            </h3>
            <AlignmentGauge pct={alignmentPct} />
            <p style={{ fontSize: '0.7rem', color: '#7A6A5A', marginTop: '1rem', lineHeight: '1.5', textAlign: 'left' }}>
              Calculado a partir de las evaluaciones guardadas. Contribución sustancial ≥ 55% pts en categoría relevante.
            </p>
          </div>

          {/* Resumen por objetivo */}
          <div style={CARD}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: '600', color: '#2C2017' }}>
              Resumen de Objetivos
            </h3>
            {objectives.map((obj, i) => {
              const cfg = STATUS_CONFIG[obj.status]
              return (
                <div key={obj.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.45rem 0', borderBottom: i < objectives.length - 1 ? '1px solid #EDE7DE' : 'none', gap: '0.5rem' }}>
                  <div style={{ fontSize: '0.75rem', color: '#2C2017', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {obj.id}. {obj.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0 }}>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.7rem', fontWeight: '600', color: cfg.color }}>
                      {obj.pctInt}%
                    </span>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.color }} />
                  </div>
                </div>
              )
            })}
          </div>

          {/* Descarga */}
          <div style={CARD}>
            <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', fontWeight: '600', color: '#2C2017' }}>
              Informe de Alineación
            </h3>
            <p style={{ fontSize: '0.75rem', color: '#7A6A5A', marginBottom: '1rem', lineHeight: '1.5' }}>
              Documento técnico con análisis de alineación por objetivo basado en las evaluaciones del proyecto.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <button onClick={handleDownloadPDF}
                style={{ width: '100%', background: '#3B2F2F', color: '#F5F0EA', border: 'none', borderRadius: '4px', padding: '0.6rem 1rem', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.85rem', fontFamily: "'DM Sans', sans-serif" }}>
                <DownloadSimple size={16} /> Descargar PDF
              </button>
              <button onClick={handleDownloadCSV}
                style={{ width: '100%', background: '#FFFFFF', color: '#5C4A3A', border: '1px solid #DDD5C8', borderRadius: '4px', padding: '0.55rem 1rem', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.82rem' }}>
                <FileCsv size={15} /> Descargar CSV
              </button>
            </div>
            {liveData && (
              <div style={{ marginTop: '0.75rem', fontSize: '0.7rem', color: '#3B7A50', background: '#F0F5EC', borderRadius: '4px', padding: '0.45rem 0.6rem', border: '1px solid #C8D8C0' }}>
                <strong>Datos reales activos:</strong>
                {liveData.sensors && <> Obj 1: {liveData.sensors.emissionsIntensity != null ? `${liveData.sensors.emissionsIntensity.toFixed(2)} kgCO₂/m²` : '—'} · Obj 3: {liveData.sensors.waterIntensity != null ? `${liveData.sensors.waterIntensity.toFixed(2)} m³/m²` : '—'}.</>}
                {liveData.ci != null && <> Obj 4 CI = {liveData.ci.toFixed(2)}.</>}
              </div>
            )}
            {liveLoading && <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#7A6A5A' }}>Cargando sensores y CI…</div>}
          </div>
        </div>
      </div>

      {/* Nota legal */}
      <div style={{ background: '#FAF7F3', border: '1px solid #DDD5C8', borderRadius: '6px', padding: '1rem 1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start' }}>
          <Warning size={16} style={{ color: '#7A6A5A', flexShrink: 0, marginTop: '2px' }} />
          <p style={{ fontSize: '0.75rem', color: '#7A6A5A', margin: 0, lineHeight: '1.6' }}>
            <strong style={{ color: '#5C4A3A' }}>Nota legal:</strong> El análisis de alineación con la Taxonomía Europea es una estimación indicativa basada en criterios BREEAM como proxy. La verificación formal de alineación Taxonomía UE requiere evaluación por Revisor Externo acreditado conforme al Reglamento (UE) 2020/852 y sus actos delegados. Este informe no tiene valor de certificación oficial.
          </p>
        </div>
      </div>
    </div>
  )
}
